import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  addHotelReservationException,
  deleteHotelReservationException,
  editHotelReservationException,
  getHotelReservationExceptionById,
  getHotelReservationExceptions,
} from '../../../../api/endpoints/corporate';
import type { CorporateHotelReservationException } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';

const schema = z.object({
  country: z.string().optional(),
  countryCode: z.string().optional(),
  minReservationDay: z.coerce.number().min(0),
  maxBudget: z.coerce.number().min(0),
  maxStar: z.coerce.number().min(0).max(5),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { country: '', countryCode: '', minReservationDay: 0, maxBudget: 0, maxStar: 0 };

function toFormValues(row: CorporateHotelReservationException): FormValues {
  return {
    country: row.country ?? '',
    countryCode: row.countryCode ?? '',
    minReservationDay: row.minReservationDay ?? 0,
    maxBudget: row.maxBudget ?? 0,
    maxStar: row.maxStar ?? 0,
  };
}

/** Otel rezervasyon istisnaları — Policy/*HotelReservationException routes. */
export function HotelReservationExceptionsPanel({ policyID }: { policyID: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporateHotelReservationException | null>(null);
  const [deleting, setDeleting] = useState<CorporateHotelReservationException | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryKey = ['corporate', 'hotelReservationExceptions', policyID];

  const query = useQuery({ queryKey, queryFn: () => getHotelReservationExceptions(policyID) });
  const rows: CorporateHotelReservationException[] = query.data?.corporateHotelReservationException ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteHotelReservationException(id),
    onSuccess: () => {
      enqueueSnackbar('İstisna silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">Otel Rezervasyon İstisnaları</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni İstisna
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ülke</TableCell>
              <TableCell>Kod</TableCell>
              <TableCell>Min. Rezervasyon Günü</TableCell>
              <TableCell>Maks. Bütçe</TableCell>
              <TableCell>Maks. Yıldız</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı istisna bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.hotelReservationExceptionID} hover>
                <TableCell>{row.country ?? '—'}</TableCell>
                <TableCell>{row.countryCode ?? '—'}</TableCell>
                <TableCell>{row.minReservationDay}</TableCell>
                <TableCell>{row.maxBudget}</TableCell>
                <TableCell>{row.maxStar}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <HotelReservationExceptionFormDialog
        open={createOpen}
        policyID={policyID}
        row={null}
        onClose={() => setCreateOpen(false)}
        queryKey={queryKey}
      />
      <HotelReservationExceptionFormDialog
        open={!!editing}
        policyID={policyID}
        row={editing}
        onClose={() => setEditing(null)}
        queryKey={queryKey}
      />

      <ConfirmDialog
        open={!!deleting}
        title="İstisnayı sil"
        description="Bu otel rezervasyon istisnasını silmek istediğinize emin misiniz?"
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.hotelReservationExceptionID)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}

function HotelReservationExceptionFormDialog({
  open,
  policyID,
  row,
  onClose,
  queryKey,
}: {
  open: boolean;
  policyID: number;
  row: CorporateHotelReservationException | null;
  onClose: () => void;
  queryKey: (string | number)[];
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!row;

  const { handleSubmit, register, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: row,
    queryKey: ['corporate', 'hotelReservationException', row?.hotelReservationExceptionID],
    fetch: async (r) =>
      (await getHotelReservationExceptionById(r.hotelReservationExceptionID)).corporateHotelReservationException?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && row
        ? editHotelReservationException({
            ...values,
            policyID,
            hotelReservationExceptionID: row.hotelReservationExceptionID,
          })
        : addHotelReservationException({ ...values, policyID }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'İstisna güncellendi.' : 'İstisna oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'İstisnayı Güncelle' : 'Yeni Otel Rezervasyon İstisnası'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Ülke" {...register('country')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Ülke Kodu" {...register('countryCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min. Rezervasyon Günü"
                {...register('minReservationDay')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Bütçe" {...register('maxBudget')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Yıldız" {...register('maxStar')} />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
